// frontend/src/pages/partner/B2CPacksPartner.jsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArchiveBoxIcon } from '@heroicons/react/24/solid';
import API from '../../services/api';
import './B2CPacksPartner.css';

/* ── couleurs icônes service (bleu palette) ────────────────── */
const SVC_COLORS = [
  { bg: 'linear-gradient(135deg,#2563eb,#0891b2)', sh: 'rgba(37,99,235,0.28)' },
  { bg: 'linear-gradient(135deg,#0891b2,#06b6d4)', sh: 'rgba(8,145,178,0.28)' },
  { bg: 'linear-gradient(135deg,#4f46e5,#2563eb)', sh: 'rgba(79,70,229,0.28)' },
  { bg: 'linear-gradient(135deg,#1d4ed8,#0e7490)', sh: 'rgba(29,78,216,0.28)' },
  { bg: 'linear-gradient(135deg,#0369a1,#0284c7)', sh: 'rgba(3,105,161,0.28)' },
  { bg: 'linear-gradient(135deg,#3b82f6,#6366f1)', sh: 'rgba(59,130,246,0.28)' },
];
const svcColor = (name) => SVC_COLORS[(name?.charCodeAt(0) || 0) % SVC_COLORS.length];

/* ─────────────────────────────────────────────────────────────
   ServicePacksModal — plein écran modal de gestion des packs
───────────────────────────────────────────────────────────── */
const ServicePacksModal = ({ service, approv, onClose, onRefreshCounts }) => {
  const [packs,        setPacks]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showCreate,   setShowCreate]   = useState(false);
  const [editingPack,  setEditingPack]  = useState(null);
  const [newPack,      setNewPack]      = useState({ name: '', points_amount: '', price: '', currency: 'TND' });
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState(null);

  const remainingPts = approv?.remaining_points ?? 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`/b2c-packs/service/${service.id}`);
      setPacks(res.data.packs || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [service.id]);

  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3500);
  };

  const handleCreate = async () => {
    if (!newPack.name || !newPack.points_amount || !newPack.price)
      return flash('error', 'Tous les champs sont requis');
    if (parseInt(newPack.points_amount) > remainingPts)
      return flash('error', `Points insuffisants (dispo : ${remainingPts.toLocaleString()})`);
    setSaving(true);
    try {
      const res = await API.post('/b2c-packs/create', {
        company_id:    service.id,
        name:          newPack.name,
        points_amount: parseInt(newPack.points_amount),
        price:         parseFloat(newPack.price),
        currency:      newPack.currency,
      });
      flash('success', res.data.message);
      setShowCreate(false);
      setNewPack({ name: '', points_amount: '', price: '', currency: 'TND' });
      await load();
      onRefreshCounts(service.id);
    } catch (err) {
      flash('error', err.response?.data?.error || 'Erreur création');
    } finally { setSaving(false); }
  };

  const handleSaveEdit = async () => {
    if (!editingPack.name || !editingPack.points_amount || !editingPack.price)
      return flash('error', 'Tous les champs sont requis');
    setSaving(true);
    try {
      const res = await API.put(`/b2c-packs/${editingPack.id}`, {
        name:          editingPack.name,
        points_amount: parseInt(editingPack.points_amount),
        price:         parseFloat(editingPack.price),
        currency:      editingPack.currency,
      });
      flash('success', res.data.message);
      setEditingPack(null);
      await load();
    } catch (err) {
      flash('error', err.response?.data?.error || 'Erreur modification');
    } finally { setSaving(false); }
  };

  const handleToggle = async (packId) => {
    try {
      const res = await API.put(`/b2c-packs/${packId}/toggle`);
      flash('success', res.data.message);
      await load();
      onRefreshCounts(service.id);
    } catch (err) {
      flash('error', err.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (packId) => {
    if (!window.confirm('Supprimer ce pack définitivement ?')) return;
    try {
      const res = await API.delete(`/b2c-packs/${packId}`);
      flash('success', res.data.message);
      await load();
      onRefreshCounts(service.id);
    } catch (err) {
      flash('error', err.response?.data?.error || 'Erreur suppression');
    }
  };

  const color = svcColor(service.name);
  const activePacks = packs.filter(p => p.is_active).length;

  return (
    <motion.div
      className="bp-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bp-svc-modal"
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.96 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="bp-svc-modal-head">
          <div className="bp-svc-modal-id">
            <div className="bp-svc-modal-icon" style={{ background: color.bg, boxShadow: `0 6px 18px ${color.sh}` }}>
              <i className="fas fa-cube" />
            </div>
            <div>
              <h2>{service.name}</h2>
              <p>
                <span className="bp-svc-pill"><i className="fas fa-layer-group" /> {packs.length} pack{packs.length !== 1 ? 's' : ''}</span>
                <span className="bp-svc-pill bp-svc-pill--green"><i className="fas fa-check-circle" /> {activePacks} actif{activePacks !== 1 ? 's' : ''}</span>
                <span className="bp-svc-pill bp-svc-pill--cyan"><i className="fas fa-coins" /> {remainingPts.toLocaleString()} pts dispo</span>
              </p>
            </div>
          </div>
          <div className="bp-svc-modal-actions">
            <button
              className="bp-btn-primary"
              onClick={() => { setShowCreate(true); setEditingPack(null); }}
              disabled={remainingPts === 0}
              title={remainingPts === 0 ? 'Approvisionnez le service avant de créer des packs' : ''}
            >
              <i className="fas fa-plus" /> Nouveau pack
            </button>
            <button className="bp-svc-modal-close" onClick={onClose}>
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        {/* ── Alert ── */}
        <AnimatePresence>
          {msg && (
            <motion.div
              className={`bp-alert bp-alert--${msg.type}`}
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              style={{ margin: '0 24px' }}
            >
              <i className={`fas ${msg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
              {msg.text}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Formulaire création / édition ── */}
        <AnimatePresence>
          {(showCreate || editingPack) && (
            <motion.div
              className="bp-inline-form"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="bp-inline-form-inner">
                <h4>
                  <i className={`fas ${editingPack ? 'fa-pen' : 'fa-plus'}`} />
                  {editingPack ? 'Modifier le pack' : 'Nouveau pack'}
                </h4>
                <div className="bp-field-row">
                  <div className="bp-field" style={{ flex: 2 }}>
                    <label>Nom du pack</label>
                    <input
                      type="text" placeholder="Ex : Pack Découverte"
                      value={editingPack ? editingPack.name : newPack.name}
                      onChange={e => editingPack
                        ? setEditingPack({ ...editingPack, name: e.target.value })
                        : setNewPack({ ...newPack, name: e.target.value })}
                    />
                  </div>
                  <div className="bp-field">
                    <label>Points {!editingPack && <span style={{ color: '#94a3b8', fontWeight: 400 }}>/ max {remainingPts.toLocaleString()}</span>}</label>
                    <input
                      type="number" min="1" max={editingPack ? undefined : remainingPts} placeholder="100"
                      value={editingPack ? editingPack.points_amount : newPack.points_amount}
                      onChange={e => editingPack
                        ? setEditingPack({ ...editingPack, points_amount: e.target.value })
                        : setNewPack({ ...newPack, points_amount: e.target.value })}
                    />
                  </div>
                  <div className="bp-field">
                    <label>Prix</label>
                    <input
                      type="number" min="0.01" step="0.01" placeholder="10.00"
                      value={editingPack ? editingPack.price : newPack.price}
                      onChange={e => editingPack
                        ? setEditingPack({ ...editingPack, price: e.target.value })
                        : setNewPack({ ...newPack, price: e.target.value })}
                    />
                  </div>
                  <div className="bp-field">
                    <label>Devise</label>
                    <select
                      value={editingPack ? editingPack.currency : newPack.currency}
                      onChange={e => editingPack
                        ? setEditingPack({ ...editingPack, currency: e.target.value })
                        : setNewPack({ ...newPack, currency: e.target.value })}
                    >
                      <option value="TND">TND</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
                <div className="bp-inline-form-footer">
                  <button className="bp-btn-ghost" onClick={() => { setShowCreate(false); setEditingPack(null); }}>
                    Annuler
                  </button>
                  <button
                    className="bp-btn-primary"
                    onClick={editingPack ? handleSaveEdit : handleCreate}
                    disabled={saving}
                  >
                    {saving
                      ? <><i className="fas fa-spinner fa-spin" /> En cours…</>
                      : editingPack
                        ? <><i className="fas fa-check" /> Enregistrer</>
                        : <><i className="fas fa-plus" /> Créer le pack</>}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Corps : liste des packs ── */}
        <div className="bp-svc-modal-body">
          {loading ? (
            <div className="bp-svc-loading">
              <div className="bp-spinner" />
              <span>Chargement…</span>
            </div>
          ) : packs.length === 0 ? (
            <div className="bp-empty-packs">
              <div className="bp-empty-packs-icon"><i className="fas fa-box-open" /></div>
              <h3>Aucun pack pour ce service</h3>
              <p>Créez votre premier pack de points</p>
              <button
                className="bp-btn-primary"
                onClick={() => setShowCreate(true)}
                disabled={remainingPts === 0}
                style={{ marginTop: 12 }}
              >
                <i className="fas fa-plus" /> Créer un pack
              </button>
            </div>
          ) : (
            <motion.div
              className="bp-grid"
              initial="hidden" animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
            >
              {packs.map(pack => {
                const ratio = pack.points_amount > 0
                  ? (parseFloat(pack.price) / pack.points_amount).toFixed(3)
                  : '—';
                return (
                  <motion.div
                    key={pack.id}
                    className={`bp-card ${!pack.is_active ? 'bp-card--inactive' : ''}`}
                    variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
                    whileHover={{ y: -3 }}
                  >
                    <div className="bp-card-top">
                      <span className={`bp-badge ${pack.is_active ? 'bp-badge--active' : 'bp-badge--inactive'}`}>
                        <i className={`fas ${pack.is_active ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
                        {pack.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <h3 className="bp-card-name">{pack.name}</h3>
                    <div className="bp-card-pts">
                      <span className="bp-card-pts-value">{pack.points_amount.toLocaleString()}</span>
                      <span className="bp-card-pts-unit">points</span>
                    </div>
                    <div className="bp-card-pricing">
                      <div className="bp-card-price">
                        <i className="fas fa-tag" />
                        {parseFloat(pack.price).toFixed(2)} {pack.currency}
                      </div>
                      <div className="bp-card-ratio">
                        <i className="fas fa-equals" />
                        {ratio} {pack.currency}/pt
                      </div>
                    </div>
                    <div className="bp-card-actions">
                      <button
                        className="bp-card-btn bp-card-btn--edit"
                        onClick={() => { setEditingPack({ id: pack.id, name: pack.name, points_amount: String(pack.points_amount), price: String(parseFloat(pack.price)), currency: pack.currency }); setShowCreate(false); }}
                      >
                        <i className="fas fa-pen" /> Modifier
                      </button>
                      <button
                        className={`bp-card-btn ${pack.is_active ? 'bp-card-btn--warn' : 'bp-card-btn--success'}`}
                        onClick={() => handleToggle(pack.id)}
                      >
                        <i className={`fas ${pack.is_active ? 'fa-eye-slash' : 'fa-eye'}`} />
                        {pack.is_active ? 'Désactiver' : 'Activer'}
                      </button>
                      <button className="bp-card-btn bp-card-btn--danger" onClick={() => handleDelete(pack.id)}>
                        <i className="fas fa-trash-alt" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────
   PAGE PRINCIPALE
───────────────────────────────────────────────────────────── */
const B2CPacksPartner = () => {
  const navigate = useNavigate();

  const [services,         setServices]         = useState([]);
  const [packCounts,       setPackCounts]       = useState({}); // { [serviceId]: { total, active } }
  const [approvisionnements, setApprovisionnements] = useState({});
  const [loading,          setLoading]          = useState(true);
  const [selectedService,  setSelectedService]  = useState(null);
  const [totalPacks,       setTotalPacks]       = useState(0);
  const [msg,              setMsg]              = useState(null);

  /* ── Chargement ─────────────────────────────────────────────── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [svcRes, approvRes] = await Promise.all([
        API.get('/companies/my'),
        API.get('/wallet/approvisionnements').catch(() => ({ data: { approvisionnements: [] } })),
      ]);

      const companies = svcRes.data.companies || [];
      setServices(companies);

      const approvMap = {};
      approvRes.data.approvisionnements?.forEach(a => { approvMap[a.company_id] = a; });
      setApprovisionnements(approvMap);

      // Charger les packs de tous les services en parallèle
      if (companies.length > 0) {
        const packsResults = await Promise.all(
          companies.map(s =>
            API.get(`/b2c-packs/service/${s.id}`)
              .then(r => ({ id: s.id, packs: r.data.packs || [] }))
              .catch(() => ({ id: s.id, packs: [] }))
          )
        );
        const counts = {};
        let tot = 0;
        packsResults.forEach(({ id, packs }) => {
          counts[id] = { total: packs.length, active: packs.filter(p => p.is_active).length };
          tot += packs.length;
        });
        setPackCounts(counts);
        setTotalPacks(tot);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* Rafraîchit uniquement le compteur d'un service après modif dans le modal */
  const refreshCounts = useCallback(async (serviceId) => {
    try {
      const res = await API.get(`/b2c-packs/service/${serviceId}`);
      const packs = res.data.packs || [];
      setPackCounts(prev => ({
        ...prev,
        [serviceId]: { total: packs.length, active: packs.filter(p => p.is_active).length },
      }));
      setTotalPacks(prev => {
        const old = packCounts[serviceId]?.total || 0;
        return prev - old + packs.length;
      });
    } catch { /* ignore */ }
  }, [packCounts]);

  /* ── Services qui ont des packs ──────────────────────────────── */
  const servicesWithPacks  = services.filter(s => (packCounts[s.id]?.total || 0) > 0);
  const servicesWithoutPacks = services.filter(s => (packCounts[s.id]?.total || 0) === 0);

  if (loading) return (
    <div className="bp-loading">
      <div className="bp-spinner" />
      <p>Chargement des packs…</p>
    </div>
  );

  return (
    <div className="bp-page">

      {/* ── Hero ───────────────────────────────────────────────── */}
      <div className="bp-hero">
        <div className="bp-hero-orb bp-hero-orb-1" />
        <div className="bp-hero-orb bp-hero-orb-2" />
        <div className="bp-hero-left">
          <div className="bp-hero-icon">
            <ArchiveBoxIcon style={{ width: 26, height: 26, color: 'white' }} />
          </div>
          <div>
            <h1>Packs B2C</h1>
            <p>Gérez les offres de points pour vos clients</p>
          </div>
        </div>
        <div className="bp-hero-right">
          <div className="bp-stat-pill">
            <i className="fas fa-layer-group" />
            <span>{totalPacks} pack{totalPacks !== 1 ? 's' : ''} au total</span>
          </div>
          <div className="bp-stat-pill">
            <i className="fas fa-cube" />
            <span>{servicesWithPacks.length} service{servicesWithPacks.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ── Alert ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {msg && (
          <motion.div
            className={`bp-alert bp-alert--${msg.type}`}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          >
            <i className={`fas ${msg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
            {msg.text}
            <button className="bp-alert-close" onClick={() => setMsg(null)}><i className="fas fa-times" /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Pas de services ────────────────────────────────────── */}
      {services.length === 0 ? (
        <div className="bp-empty-service">
          <div className="bp-empty-service-icon"><i className="fas fa-cube" /></div>
          <h3>Aucun service créé</h3>
          <p>Créez un service avant de gérer ses packs B2C</p>
          <button className="bp-btn-primary" style={{ marginTop: 16 }}
            onClick={() => navigate('/dashboard/partner/services')}>
            <i className="fas fa-plus" /> Créer un service
          </button>
        </div>
      ) : (
        <>
          {/* ── Services avec packs ──────────────────────────── */}
          {servicesWithPacks.length > 0 && (
            <section className="bp-section">
              <div className="bp-section-header">
                <h2><i className="fas fa-layer-group" /> Services avec packs</h2>
                <span className="bp-section-count">{servicesWithPacks.length}</span>
              </div>
              <motion.div
                className="bp-svc-grid"
                initial="hidden" animate="visible"
                variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
              >
                {servicesWithPacks.map(svc => {
                  const col   = svcColor(svc.name);
                  const cnt   = packCounts[svc.id] || { total: 0, active: 0 };
                  const approv = approvisionnements[svc.id];
                  const pts   = approv?.remaining_points ?? 0;
                  return (
                    <motion.div
                      key={svc.id}
                      className="bp-svc-card"
                      variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                      whileHover={{ y: -4, boxShadow: '0 14px 36px rgba(37,99,235,0.14)' }}
                      onClick={() => setSelectedService(svc)}
                    >
                      <div className="bp-svc-card-top">
                        <div className="bp-svc-icon" style={{ background: col.bg, boxShadow: `0 6px 16px ${col.sh}` }}>
                          <i className="fas fa-cube" />
                        </div>
                        <span className="bp-svc-badge-packs">
                          <i className="fas fa-layer-group" /> {cnt.total} pack{cnt.total !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <h3 className="bp-svc-name">{svc.name}</h3>
                      <div className="bp-svc-meta">
                        <span className="bp-svc-meta-item bp-svc-meta--green">
                          <i className="fas fa-check-circle" /> {cnt.active} actif{cnt.active !== 1 ? 's' : ''}
                        </span>
                        <span className="bp-svc-meta-item bp-svc-meta--blue">
                          <i className="fas fa-coins" /> {pts.toLocaleString()} pts
                        </span>
                      </div>
                      <div className="bp-svc-card-cta">
                        <i className="fas fa-arrow-right" /> Gérer les packs
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </section>
          )}

          {/* ── Services sans packs ──────────────────────────── */}
          {servicesWithoutPacks.length > 0 && (
            <section className="bp-section">
              <div className="bp-section-header">
                <h2><i className="fas fa-cube" /> Services sans packs</h2>
                <span className="bp-section-count bp-section-count--muted">{servicesWithoutPacks.length}</span>
              </div>
              <motion.div
                className="bp-svc-grid"
                initial="hidden" animate="visible"
                variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
              >
                {servicesWithoutPacks.map(svc => {
                  const col   = svcColor(svc.name);
                  const approv = approvisionnements[svc.id];
                  const pts   = approv?.remaining_points ?? 0;
                  return (
                    <motion.div
                      key={svc.id}
                      className="bp-svc-card bp-svc-card--empty"
                      variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
                      whileHover={{ y: -3 }}
                      onClick={() => setSelectedService(svc)}
                    >
                      <div className="bp-svc-card-top">
                        <div className="bp-svc-icon bp-svc-icon--muted" style={{ background: col.bg, boxShadow: `0 4px 12px ${col.sh}` }}>
                          <i className="fas fa-cube" />
                        </div>
                        <span className="bp-svc-badge-empty">Aucun pack</span>
                      </div>
                      <h3 className="bp-svc-name">{svc.name}</h3>
                      <div className="bp-svc-meta">
                        <span className="bp-svc-meta-item bp-svc-meta--blue">
                          <i className="fas fa-coins" /> {pts.toLocaleString()} pts
                        </span>
                      </div>
                      <div className="bp-svc-card-cta bp-svc-card-cta--muted">
                        <i className="fas fa-plus" /> Créer des packs
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </section>
          )}
        </>
      )}

      {/* ── Modal packs d'un service ───────────────────────────── */}
      <AnimatePresence>
        {selectedService && (
          <ServicePacksModal
            service={selectedService}
            approv={approvisionnements[selectedService.id]}
            onClose={() => setSelectedService(null)}
            onRefreshCounts={refreshCounts}
          />
        )}
      </AnimatePresence>

    </div>
  );
};

export default B2CPacksPartner;
